import { FC, useState } from 'react';
import { Dialog, Portal, CloseButton } from '@chakra-ui/react';
import { IInjury } from '../../../models/injury';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import InjuryRegistrationForm, { PrefillInjuryData } from './InjuryRegistrationForm';
import styles from './InjuryRegistrationModal.module.scss';

/** ID HTML del form — collega il pulsante Submit nel Dialog.Footer al form nel Dialog.Body */
const FORM_ID = 'injury-registration-form';

interface InjuryRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  athleteId: string;
  teamId: string;
  editingInjury?: IInjury;
  prefillData?: PrefillInjuryData | null;
  onSuccess?: (injury: IInjury) => void;
}

export const InjuryRegistrationModal: FC<InjuryRegistrationModalProps> = ({
  isOpen,
  onClose,
  athleteId,
  teamId,
  editingInjury,
  prefillData,
  onSuccess,
}) => {
  const title = editingInjury ? 'Modifica infortunio' : prefillData ? 'Apri infortunio' : 'Registra infortunio';
  const submitLabel = editingInjury ? 'Aggiorna' : 'Registra infortunio';

  // Stato submitting propagato dal form tramite onSubmittingChange
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSuccess = (injury: IInjury) => {
    onSuccess?.(injury);
    onClose();
  };

  // CustomButton non espone una prop `form`: il submit dal footer esterno viene
  // triggerato chiamando requestSubmit() sul form nel Dialog.Body, equivalente
  // funzionale dell'attributo HTML form="..." su un <button type="submit">.
  const submitExternalForm = () => {
    (document.getElementById(FORM_ID) as HTMLFormElement | null)?.requestSubmit();
  };

  return (
    <Dialog.Root
      size="xl"
      placement="center"
      scrollBehavior="inside"
      open={isOpen}
      onOpenChange={(e) => { if (!e.open) onClose(); }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          {/* scrollBehavior="inside" fa scrollare solo Dialog.Body; Header e Footer restano fissi */}
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title className={styles.title}>
                {title}
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <InjuryRegistrationForm
                athleteId={athleteId}
                teamId={teamId}
                editingInjury={editingInjury}
                prefillData={prefillData}
                onSuccess={handleSuccess}
                formId={FORM_ID}
                hideButtons
                onSubmittingChange={setIsSubmitting}
              />
            </Dialog.Body>
            {/* Footer fisso con i pulsanti — visibile anche quando il body scrolla */}
            <Dialog.Footer className={styles.footer}>
              <CustomButton color="outline" type="button" onClick={onClose}>
                Annulla
              </CustomButton>
              <CustomButton
                color="secondary"
                type="button"
                disabled={isSubmitting}
                isLoading={isSubmitting}
                onClick={submitExternalForm}
              >
                {submitLabel}
              </CustomButton>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default InjuryRegistrationModal;
